<script lang="ts" setup>
import Paypal from '../gateways/Paypal.vue';
import WirePayment from '../gateways/WirePayment.vue';
import { CreditCard } from 'lucide-vue-next';

type Module = { module_name: string; additionalInformation: string };
const { data: payments }: any = usePaymentMethods();
const { data: cart } = useCart();

const route = useRoute();
const refError = ref<HTMLElement | null>(null);
const error = computed(() => route.query['error'] ?? null);
const { t } = useI18n();

const isOrderFree = computed(() => {
  return cart.value?.totals?.total?.amount === 0;
});

const paypalGateway = computed(() => {
  return Object.values(payments.value ?? {})
    .flat()
    .find((method: any) => method?.module_name === 'paypal') as Module | undefined;
});

const wirePaymentGateway = computed(() => {
  return Object.values(payments.value ?? {})
    .flat()
    .find((method: any) => method?.module_name === 'ps_wirepayment') as Module | undefined;
});

const selectedPaymentMethod = ref<'paypal' | 'wirepayment'>('paypal');

const bothGatewaysAvailable = computed(() => {
  return paypalGateway.value && wirePaymentGateway.value;
});

onMounted(() => {
  setTimeout(() => {
    if (error.value && refError.value) {
      window.scrollTo({ top: refError.value.offsetTop, behavior: 'smooth' });
    }
  }, 500);
  
  if (isOrderFree.value) {
    selectedPaymentMethod.value = 'wirepayment';
  } else if (paypalGateway.value && !wirePaymentGateway.value) {
    selectedPaymentMethod.value = 'paypal';
  } else if (wirePaymentGateway.value && !paypalGateway.value) {
    selectedPaymentMethod.value = 'wirepayment';
  } else if (paypalGateway.value) {
    selectedPaymentMethod.value = 'paypal';
  }
});
</script>

<template>
  <div ref="refError">
    <Alert v-show="error" variant="destructive" class="w-full mb-8">
      <AlertDescription>
        {{ error }}
      </AlertDescription>
    </Alert>

    <div v-if="!isOrderFree && bothGatewaysAvailable" class="mb-6">
      <h3 class="text-lg font-medium mb-4">{{ t('Select payment method') }}</h3>
      <div class="grid gap-4">
        <label class="relative flex items-center p-4 border rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
               :class="{ 'border-primary-500 bg-primary-50': selectedPaymentMethod === 'paypal' }">
          <input 
            type="radio" 
            name="payment_method" 
            value="paypal" 
            v-model="selectedPaymentMethod"
            class="sr-only"
          />
          <div class="flex items-center gap-3 w-full">
            <div class="w-6 h-6 border-2 rounded-full flex items-center justify-center"
                 :class="selectedPaymentMethod === 'paypal' ? 'border-primary-500' : 'border-gray-300'">
              <div v-if="selectedPaymentMethod === 'paypal'" class="w-3 h-3 bg-primary-500 rounded-full"></div>
            </div>
            <span class="font-medium">{{ t('PayPal / Credit Card') }}</span>
            <svg class="h-6 ml-auto" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 3.993-.028.15a.804.804 0 0 1-.793.679H8.032a.494.494 0 0 1-.488-.589l1.722-10.91a.805.805 0 0 1 .794-.68h2.456c3.238 0 5.774-1.314 6.513-5.12a4.23 4.23 0 0 0-.063-2.07A3.994 3.994 0 0 0 16.238 1H7.85a.805.805 0 0 0-.794.68L4.61 16.32a.494.494 0 0 0 .488.589h3.037l.762-4.833-.024.15" fill="#003087"/>
              <path d="M9.554 5.239A.805.805 0 0 1 10.348 4.559h6.423c.766 0 1.473.142 2.078.425.238.109.456.24.653.389.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 3.993-.028.15a.804.804 0 0 1-.793.679H8.032a.494.494 0 0 1-.488-.589l1.722-10.91z" fill="#0070E0"/>
            </svg>
          </div>
        </label>

        <label class="relative flex items-center p-4 border rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
               :class="{ 'border-primary-500 bg-primary-50': selectedPaymentMethod === 'wirepayment' }">
          <input 
            type="radio" 
            name="payment_method" 
            value="wirepayment" 
            v-model="selectedPaymentMethod"
            class="sr-only"
          />
          <div class="flex items-center gap-3 w-full">
            <div class="w-6 h-6 border-2 rounded-full flex items-center justify-center"
                 :class="selectedPaymentMethod === 'wirepayment' ? 'border-primary-500' : 'border-gray-300'">
              <div v-if="selectedPaymentMethod === 'wirepayment'" class="w-3 h-3 bg-primary-500 rounded-full"></div>
            </div>
            <span class="font-medium">{{ t('Bank Transfer') }}</span>
            <CreditCard class="h-5 w-5 ml-auto text-gray-600" />
          </div>
        </label>
      </div>
    </div>

    <div class="mt-6">
      <Paypal v-if="!isOrderFree && paypalGateway && (selectedPaymentMethod === 'paypal' || (!wirePaymentGateway && paypalGateway))" :currentGateway="paypalGateway" />
      <WirePayment v-if="wirePaymentGateway && (selectedPaymentMethod === 'wirepayment' || (!paypalGateway && wirePaymentGateway) || isOrderFree)" :isOrderFree="isOrderFree" />
    </div>
  </div>
</template>
